import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Snackbar from '@mui/material/Snackbar';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import Typography from '@mui/material/Typography';
import { sendFormEmails } from '../services/emailService';
import { mockFetch } from '../services/mockEmailApi'; // Import mockFetch for development
import { testBackendConnection } from '../services/testConnection';
import { saveFormData } from '../services/databaseService';
import { checkExistingPatient, getActivePractices } from '../services/patientService';
import Step1PatientInfo from './Step1_PatientInfo';
import Step2Insurance from './Step2_Insurance';
import Step3MedicalHistory from './Step3_MedicalHistory';
import Step4Concerns from './Step4_Concerns';
import StepOtherHistory from './StepOtherHistory';
import LanguageSwitcher from './LanguageSwitcher';
import useDobConfirm from '../hooks/useDobConfirm';
import DobConfirmDialog from './DobConfirmDialog';

// Step keys for internal use - labels will be translated
const stepKeys = ['patientInfo', 'insurance', 'medicalHistory', 'otherHistory', 'concerns'];


function validateStep(formData, step) {
  switch (step) {
    case 0: // Patient Info
      let isValidDOB = false;
      if (formData.date_of_birth) {
        const dob = new Date(formData.date_of_birth);
        const minDate = new Date();
        minDate.setFullYear(minDate.getFullYear() - 105); // Max 105 years old
        const today = new Date();
        today.setHours(0, 0, 0, 0); // Compare date part only
        isValidDOB = dob >= minDate && dob <= today;
      }
      return !!(
        formData.practice &&
        formData.name && formData.name.trim() !== '' &&
        formData.email && /^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/.test(formData.email) &&
        formData.date_of_birth && isValidDOB &&
        formData.cell_phone && formData.cell_phone.trim() !== '' &&
        formData.preferred_pharmacy && // Just check if pharmacy is selected
        formData.gender
      );
    case 1: // Insurance
      if (formData.has_insurance === 'Yes') {
        return !!(
          formData.primary_insurance && formData.primary_insurance.trim() !== '' &&
          formData.member_id && formData.member_id.trim() !== '' &&
          formData.subscriber_name && formData.subscriber_name.trim() !== '' &&
          formData.subscriber_dob
        );
      } else if (formData.has_insurance === "I'm an attorney or workman's compensation patient") {
        return !!(
          formData.attorney_name && formData.attorney_name.trim() !== '' &&
          formData.adjuster_name && formData.adjuster_name.trim() !== ''
        );
      }
      return formData.has_insurance === 'No'; // Explicitly check for 'No' or 'Yes'
    case 2: // Medical History
      // First check if allergies question is answered
      if (!formData.has_allergies || !['Yes', 'No'].includes(formData.has_allergies)) {
        return false;
      }

      // Then check if allergies details are provided when needed
      if (formData.has_allergies === 'Yes' && 
          (!formData.allergies_list || formData.allergies_list.length === 0) && 
          (!formData.allergies || formData.allergies.trim() === '')) {
        return false;
      }

      // Check if medical problems 'Other' has details
      if (formData.medical_problems && formData.medical_problems.includes('Other')) {
        if ((!formData.other_medical_problems || formData.other_medical_problems.length === 0) && 
            (!formData.other_medical_problem || formData.other_medical_problem.trim() === '')) {
          return false;
        }
      }

      // Check if taking medication is answered
      if (!formData.taking_medication || !['Yes', 'No'].includes(formData.taking_medication)) {
        return false;
      }

      // If taking medication is Yes, check if medications are listed
      if (formData.taking_medication === 'Yes' && 
          (!formData.medications_list || formData.medications_list.length === 0) && 
          (!formData.medications || formData.medications.trim() === '')) {
        return false;
      }

      return true;
    case 3: // Other History
      // All fields are optional or have defaults, so generally true.
      // Add specific validations if any field becomes strictly mandatory.
      return true;
    case 4: // Concerns
      return !!(
        formData.concerns && 
        formData.concerns.length > 0
      );
    default:
      return true; // Should not happen if steps are well-defined
  }
}

function getFieldErrors(formData, step) {
  const errors = {};
  
  switch (step) {
    case 0: // Patient Info
      if (!formData.practice) {
        errors.practice = 'Practice is required';
      }
      if (!formData.name || formData.name.trim() === '') {
        errors.name = 'Full name is required';
      }
      if (!formData.email) {
        errors.email = 'Email is required';
      } else if (!/^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/.test(formData.email)) {
        errors.email = 'Please enter a valid email address';
      }
      if (!formData.date_of_birth) {
        errors.date_of_birth = 'Date of birth is required';
      } else {
        const dob = new Date(formData.date_of_birth);
        const minDate = new Date();
        minDate.setFullYear(minDate.getFullYear() - 105);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        if (dob < minDate || dob > today) {
          errors.date_of_birth = 'Date of birth must be between 0-105 years old';
        }
      }
      if (!formData.cell_phone || formData.cell_phone.trim() === '') {
        errors.cell_phone = 'Cell phone number is required';
      }
      if (!formData.preferred_pharmacy) {
        errors.preferred_pharmacy = 'Preferred pharmacy is required';
      }
      if (!formData.gender) {
        errors.gender = 'Gender is required';
      }
      break;
      
    case 1: // Insurance
      if (!formData.has_insurance || !['Yes', 'No', "I'm an attorney or workman's compensation patient"].includes(formData.has_insurance)) {
        errors.has_insurance = 'Please select an insurance option';
      } else if (formData.has_insurance === 'Yes') {
        if (!formData.primary_insurance || formData.primary_insurance.trim() === '') {
          errors.primary_insurance = 'Primary insurance is required';
        }
        if (!formData.member_id || formData.member_id.trim() === '') {
          errors.member_id = 'Member ID is required';
        }
        if (!formData.subscriber_name || formData.subscriber_name.trim() === '') {
          errors.subscriber_name = 'Subscriber name is required';
        }
        if (!formData.subscriber_dob) {
          errors.subscriber_dob = 'Subscriber date of birth is required';
        }
      } else if (formData.has_insurance === "I'm an attorney or workman's compensation patient") {
        if (!formData.attorney_name || formData.attorney_name.trim() === '') {
          errors.attorney_name = 'Attorney name is required';
        }
        if (!formData.adjuster_name || formData.adjuster_name.trim() === '') {
          errors.adjuster_name = 'Adjuster name is required';
        }
      }
      break;
      
    case 2: // Medical History
      if (!formData.has_allergies || !['Yes', 'No'].includes(formData.has_allergies)) {
        errors.has_allergies = 'Please answer if you have allergies';
      } else if (formData.has_allergies === 'Yes') {
        if ((!formData.allergies_list || formData.allergies_list.length === 0) && 
            (!formData.allergies || formData.allergies.trim() === '')) {
          errors.allergies = 'Please specify your allergies';
        }
      }
      
      if (formData.medical_problems && formData.medical_problems.includes('Other')) {
        if ((!formData.other_medical_problems || formData.other_medical_problems.length === 0) && 
            (!formData.other_medical_problem || formData.other_medical_problem.trim() === '')) {
          errors.other_medical_problem = 'Please specify other medical problems';
        }
      }
      
      if (!formData.taking_medication || !['Yes', 'No'].includes(formData.taking_medication)) {
        errors.taking_medication = 'Please answer if you are taking medications';
      } else if (formData.taking_medication === 'Yes') {
        if ((!formData.medications_list || formData.medications_list.length === 0) && 
            (!formData.medications || formData.medications.trim() === '')) {
          errors.medications = 'Please list your medications';
        }
      }
      break;
      
    case 4: // Concerns
      if (!formData.concerns || formData.concerns.length === 0) {
        errors.concerns = 'Please select at least one concern';
      }
      break;
  }
  
  return errors;
}

export default function MultiStepForm() {
  const { t } = useTranslation('common');
  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState({});
  
  // Get translated step labels
  const steps = stepKeys.map(key => t(`steps.${key}`));
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  // Existing patient check state
  const [showExistingPatientDialog, setShowExistingPatientDialog] = useState(false);
  const [existingPatientRecord, setExistingPatientRecord] = useState(null);
  const [isCheckingExistingPatient, setIsCheckingExistingPatient] = useState(false);
  const [hasCheckedForDuplicate, setHasCheckedForDuplicate] = useState(false);
  const [lockedPracticeName, setLockedPracticeName] = useState(null);
  const [showSummaryPrompt, setShowSummaryPrompt] = useState(false);
  // DOB confirmation — formData.date_of_birth is managed via handleChange,
  // so we use a thin wrapper that the hook can call as a raw setter.
  const setDobRaw = (val) => setFormData(prev => ({ ...prev, date_of_birth: val }));
  const { dobInputRef, showDobConfirmDialog, handleDobBlur, confirmDob, fixDob } = useDobConfirm(formData.date_of_birth, setDobRaw);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('practice');
    const prefill = {
      name: params.get('name'),
      date_of_birth: params.get('dob'),
      gender: params.get('gender'),
      cell_phone: params.get('phone')?.replace(/\D/g, '').slice(-10),
      email: params.get('email'),
      street_address: params.get('street'),
      city: params.get('city'),
      state: params.get('state'),
      zip_code: params.get('zip'),
    };
    setFormData((prev) => ({
      ...prev,
      ...Object.fromEntries(Object.entries(prefill).filter(([, value]) => value)),
    }));
    if (!code) return;
    const normalized = String(code).toUpperCase();

    const applyLocked = (name) => {
      setLockedPracticeName(name);
      setFormData((prev) => ({ ...prev, practice: name }));
    };

    const tryMapFromApi = async () => {
      try {
        const practices = await getActivePractices();
        const match = practices.find(p => (p.practice_code || '').toUpperCase() === normalized);
        if (match && match.practice_name) {
          applyLocked(match.practice_name);
          return;
        }
      } catch (_) {}
      const fallbackMap = { SAGS: 'San Antonio General Surgery', STARS: 'STARS Plastic Surgery' };
      if (fallbackMap[normalized]) {
        applyLocked(fallbackMap[normalized]);
      }
    };

    tryMapFromApi();
  }, []);

  // Effect to check for existing patient when key fields are completed
  useEffect(() => {
    const { practice, name, date_of_birth, gender, cell_phone } = formData;
    
    // Helper function to check if phone number is complete (at least 10 digits)
    const isPhoneComplete = (phone) => {
      if (!phone) return false;
      // Remove all non-digit characters and check length
      const digitsOnly = phone.replace(/\D/g, '');
      return digitsOnly.length >= 10;
    };
    
    // Only check if we're on step 0, all key fields are filled, AND phone number is complete
    if (activeStep === 0 && 
        practice && 
        name && 
        date_of_birth && 
        gender && 
        cell_phone && 
        isPhoneComplete(cell_phone)) {
      
      // Add a small delay to debounce rapid changes (e.g., if user types quickly)
      const timeoutId = setTimeout(() => {
        checkForExistingPatient(practice, name, date_of_birth, gender, cell_phone);
      }, 500); // 500ms delay to allow user to finish typing

      // Cleanup timeout if component unmounts or dependencies change
      return () => clearTimeout(timeoutId);
    }
    
    // Reset duplicate check flag if any key field changes or phone is incomplete
    if (!practice || !name || !date_of_birth || !gender || !isPhoneComplete(cell_phone)) {
      setHasCheckedForDuplicate(false);
    }
  }, [formData.practice, formData.name, formData.date_of_birth, formData.gender, formData.cell_phone, activeStep]);

  const handleNext = () => {
    if (!validateStep(formData, activeStep)) {
      const errors = getFieldErrors(formData, activeStep);
      setFieldErrors(errors);
      if (activeStep === 0 && formData.date_of_birth) {
        const dob = new Date(formData.date_of_birth);
        const minDate = new Date();
        minDate.setFullYear(minDate.getFullYear() - 105);
        const today = new Date();
        today.setHours(0,0,0,0);
        if (dob < minDate || dob > today) {
          setError(t('errors.invalidDOB'));
          return;
        }
      }
      setError(t('errors.requiredFields'));
      return;
    }
    setError('');
    setFieldErrors({});
    setActiveStep((prev) => prev + 1);
  };


  const handleBack = () => {
    setError('');
    setActiveStep((prev) => prev - 1);
  };

  const handleChange = (data) => {
    setError('');
    // Clear errors for fields that are being updated
    setFieldErrors(prev => {
      const newErrors = { ...prev };
      Object.keys(data).forEach(key => {
        if (data[key] !== undefined && data[key] !== null && data[key] !== '') {
          delete newErrors[key];
        }
      });
      return newErrors;
    });
    setFormData((prev) => {
      const newData = { ...prev };
      // Update the changed fields
      Object.keys(data).forEach(key => {
        // Always update if the field has a value
        if (data[key] !== undefined && data[key] !== null) {
          newData[key] = data[key];
        }
      });
      return newData;
    });
  };

  // Check for existing patient when key fields are completed
  const checkForExistingPatient = async (practice, name, dateOfBirth, gender, cellPhone) => {
    if (!practice || !name || !dateOfBirth || !gender || !cellPhone) {
      return; // Not all required fields filled yet
    }
    
    if (hasCheckedForDuplicate) {
      return; // Already checked for this combination
    }

    console.log('🔍 FRONTEND: Starting existing patient check...');
    console.log('📋 Search criteria:', {
      practice,
      name,
      date_of_birth: dateOfBirth,
      gender,
      cell_phone: cellPhone
    });
    
    setIsCheckingExistingPatient(true);
    setError(''); // Clear any previous errors
    
    try {
      const result = await checkExistingPatient(
        practice, 
        name, 
        dateOfBirth, 
        gender, 
        cellPhone
      );
      
      console.log('📡 FRONTEND: API response received:', result);
      
      if (result.success && result.found) {
        console.log('✅ FRONTEND: Match found! Showing dialog to user');
        setExistingPatientRecord(result.record);
        setShowExistingPatientDialog(true);
        setSnackbarMessage(`Existing record found for ${name}. Choose whether to load previous data.`);
        setSnackbarOpen(true);
      } else if (result.success && !result.found) {
        console.log('❌ FRONTEND: No match found - user is new or has different details');
        setSnackbarMessage('No existing record found. Proceeding with new patient intake.');
        setSnackbarOpen(true);
      } else {
        console.warn('⚠️ FRONTEND: API call succeeded but result format unexpected:', result);
        setError('Unable to check for existing records. Please continue with form submission.');
      }
      
      setHasCheckedForDuplicate(true);
    } catch (error) {
      console.error('💥 FRONTEND ERROR: Failed to check for existing patient:', error);
      setError(`Unable to check existing records: ${error.message}. You may continue with the form.`);
      setHasCheckedForDuplicate(true);
      setSnackbarMessage('Error checking for existing records. Continuing as new patient.');
      setSnackbarOpen(true);
    } finally {
      setIsCheckingExistingPatient(false);
      console.log('🏁 FRONTEND: Existing patient check completed');
    }
  };

  // Handle user's decision about pre-populating data
  const handlePrePopulateDecision = (shouldPrePopulate) => {
    if (shouldPrePopulate && existingPatientRecord) {
      // Pre-populate form data with existing record, but preserve the key identifying fields
      const prePopulatedData = {
        ...existingPatientRecord,
        // Keep the current values for key identifying fields
        practice: formData.practice,
        name: formData.name,
        date_of_birth: formData.date_of_birth,
        gender: formData.gender,
        cell_phone: formData.cell_phone
      };
      
      // Convert back some field mappings to match form structure
      if (prePopulatedData.smoking_amount) {
        prePopulatedData.smoking_how_much = prePopulatedData.smoking_amount;
      }
      if (prePopulatedData.alcohol_frequency) {
        prePopulatedData.alcohol_how_often = prePopulatedData.alcohol_frequency;
      }
      if (prePopulatedData.drugs) {
        prePopulatedData.drug_use = prePopulatedData.drugs;
      }
      if (prePopulatedData.drugs_explain) {
        prePopulatedData.drug_use_details = prePopulatedData.drugs_explain;
      }
      if (prePopulatedData.other_medical_problems_list) {
        prePopulatedData.other_medical_problems = prePopulatedData.other_medical_problems_list;
      }
      
      // Remove Step4_Concerns data so it must be filled fresh each time
      delete prePopulatedData.conversation;
      delete prePopulatedData.concerns_conversation;
      delete prePopulatedData.pain_level;
      delete prePopulatedData.pain_description;
      delete prePopulatedData.treatment_expectations;
      
      // Format date fields to yyyy-MM-dd format for HTML date inputs
      const formatDateForInput = (dateString) => {
        if (!dateString) return '';
        try {
          const date = new Date(dateString);
          return date.toISOString().split('T')[0]; // Extract yyyy-MM-dd part
        } catch (error) {
          console.warn('Date formatting error:', error, 'for date:', dateString);
          return '';
        }
      };
      
      // Format subscriber date of birth fields
      if (prePopulatedData.subscriber_dob) {
        prePopulatedData.subscriber_dob = formatDateForInput(prePopulatedData.subscriber_dob);
      }
      if (prePopulatedData.secondary_subscriber_dob) {
        prePopulatedData.secondary_subscriber_dob = formatDateForInput(prePopulatedData.secondary_subscriber_dob);
      }
      
      console.log('Date formatting completed:');
      console.log('subscriber_dob formatted:', prePopulatedData.subscriber_dob);
      console.log('secondary_subscriber_dob formatted:', prePopulatedData.secondary_subscriber_dob);
      
      setFormData(prePopulatedData);
      setSnackbarMessage('Your previous information has been loaded!');
      setSnackbarOpen(true);
    }
    
    setShowExistingPatientDialog(false);
    setExistingPatientRecord(null);
  };

const doSubmit = async () => {
  // Final validation before submitting all steps
  for (let i = 0; i < steps.length; i++) {
    if (!validateStep(formData, i)) {
      setActiveStep(i); // Go to the step with missing info
      setError(`Please complete all required fields in the '${steps[i]}' step before submitting.`);
      return;
    }
  }

  setIsSubmitting(true);
  setError('');
  setSubmitSuccess(false);

  try {
    // Test connection to API server
    const apiTest = await testBackendConnection();
    console.log('API server connection test result:', apiTest);

    if (!apiTest.success) {
      console.warn('Backend connection failed, attempting to use production API endpoint anyway');
      // Continue with form submission anyway
    }

    // First, save form data to the database
    const dbResult = await saveFormData(formData);
    console.log('Database save result:', dbResult);

    if (!dbResult.success) {
      throw new Error(dbResult.error || 'Failed to save form data to database');
    }

    // Then, send emails
    const emailResult = await sendFormEmails(formData);
    console.log('Email sending result:', emailResult);

    if (emailResult.success) {
      setSubmitSuccess(true);
      setSnackbarMessage('Form submitted successfully! Data saved and confirmation emails sent.');
    } else {
      // If emails fail but database save succeeded, still consider it partially successful
      setSubmitSuccess(true);
      setError('Your form data was saved, but there was an issue sending confirmation emails.');
      setSnackbarMessage('Form data saved, but email sending failed.');
    }
  } catch (error) {
    console.error('Form submission error:', error);
    setError(`Error: ${error.message || 'Failed to submit form. Please try again later.'}`);
    setSnackbarMessage('Form submission failed. Please try again.');
  } finally {
    setIsSubmitting(false);
    setSnackbarOpen(true);
  }
};

// Wrapper to encourage completing questions if no medical summary
const attemptSubmit = () => {
  if (!formData.medical_narrative || (typeof formData.medical_narrative === 'string' && formData.medical_narrative.trim() === '')) {
    setShowSummaryPrompt(true);
    return;
  }
  doSubmit();
};


  const stepProps = { formData, onChange: handleChange, fieldErrors };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };
  
  return (
    <Box sx={{ width: '100%' }}>

      <Stepper activeStep={activeStep} alternativeLabel sx={{ clear: 'both', mb: 2 }}>
        {steps.map((label, idx) => (
          <Step key={label}>
            <StepLabel
              onClick={() => {
                // Allow navigation to already visited/validated steps or current step
                if (idx <= activeStep) {
                    setActiveStep(idx);
                } else {
                    // Check if all previous steps up to current activeStep are valid before jumping forward
                    let canNavigate = true;
                    for (let i = 0; i < activeStep; i++) { // Validate up to, but not including, the current activeStep
                        if (!validateStep(formData, i)) {
                            canNavigate = false;
                            break;
                        }
                    }
                    // Then check current step if trying to jump beyond it
                    if (canNavigate && validateStep(formData, activeStep)) {
                        // And then check all steps between current and target idx
                        for (let i = activeStep + 1; i < idx; i++) {
                            if (!validateStep(formData, i)) {
                                canNavigate = false;
                                break;
                            }
                        }
                        if (canNavigate) {
                            setActiveStep(idx);
                        } else {
                            setError(t('errors.completeIntermediateSteps'));
                        }
                    } else if (!validateStep(formData, activeStep)){
                        setError(t('errors.completeCurrentStep'));
                    } else {
                         setError(t('errors.completePreviousSteps'));
                    }
                }
              }}
              style={{ cursor: 'pointer'}}
            >
              {label}
            </StepLabel>
          </Step>
        ))}
      </Stepper>
      
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        message={snackbarMessage}
      />
      
      {/* Existing Patient Confirmation Dialog */}
      <Dialog
        open={showExistingPatientDialog}
        onClose={() => handlePrePopulateDecision(false)}
        aria-labelledby="existing-patient-dialog-title"
        aria-describedby="existing-patient-dialog-description"
      >
        <DialogTitle id="existing-patient-dialog-title">
          {t('dialogs.existingPatient.title')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="existing-patient-dialog-description">
            {t('dialogs.existingPatient.message', { name: formData.name })}
          </DialogContentText>
          {isCheckingExistingPatient && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <CircularProgress size={24} />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => handlePrePopulateDecision(false)} 
            disabled={isCheckingExistingPatient}
          >
            {t('dialogs.existingPatient.noStartFresh')}
          </Button>
          <Button 
            onClick={() => handlePrePopulateDecision(true)} 
            variant="contained" 
            disabled={isCheckingExistingPatient}
          >
            {t('dialogs.existingPatient.yesLoadInfo')}
          </Button>
        </DialogActions>
      </Dialog>

      <DobConfirmDialog open={showDobConfirmDialog} dob={formData.date_of_birth} onFix={fixDob} onConfirm={confirmDob} />

      {/* Encourage completing questions dialog */}
      <Dialog
        open={showSummaryPrompt}
        onClose={() => setShowSummaryPrompt(false)}
        aria-labelledby="summary-prompt-title"
        aria-describedby="summary-prompt-description"
      >
        <DialogTitle id="summary-prompt-title">{t('dialogs.summaryPrompt.title')}</DialogTitle>
        <DialogContent>
          <DialogContentText id="summary-prompt-description">
            {t('dialogs.summaryPrompt.message')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setShowSummaryPrompt(false); setActiveStep(4); }}>{t('dialogs.summaryPrompt.goBack')}</Button>
          <Button onClick={() => { setShowSummaryPrompt(false); doSubmit(); }} variant="contained" color="primary">
            {t('dialogs.summaryPrompt.submitAnyway')}
          </Button>
        </DialogActions>
      </Dialog>
      <Box sx={{ my: 4 }}>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {submitSuccess ? (
          <>
            <Alert severity="success" sx={{ mb: 2 }}>
              {t('success.submission')}
            </Alert>
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2" gutterBottom>
                <strong>{t('success.nextStep')}</strong> {t('success.completeConsents')}
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                {t('success.consentsDescription')}
              </Typography>
              <Button 
                variant="contained" 
                color="primary" 
                size="small"
                href={`/rxconsent?practice=${encodeURIComponent(formData.practice || '')}&name=${encodeURIComponent(formData.name || '')}&dob=${encodeURIComponent(formData.date_of_birth || '')}&gender=${encodeURIComponent(formData.gender || '')}&phone=${encodeURIComponent(formData.cell_phone || '')}&email=${encodeURIComponent(formData.email || '')}`}
                sx={{ mt: 1 }}
              >
                {t('success.goToConsents')}
              </Button>
            </Alert>
          </>
        ) : (
          <>
            {activeStep === 0 && (
              <>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                  <LanguageSwitcher compact />
                </Box>
                <Step1PatientInfo {...stepProps} lockedPractice={lockedPracticeName} fieldErrors={fieldErrors} onDobBlur={handleDobBlur} dobInputRef={dobInputRef} />
              </>
            )} 
            {activeStep === 1 && <Step2Insurance {...stepProps} fieldErrors={fieldErrors} />}
            {activeStep === 2 && <Step3MedicalHistory {...stepProps} fieldErrors={fieldErrors} />}
            {activeStep === 3 && <StepOtherHistory {...stepProps} fieldErrors={fieldErrors} />}
            {activeStep === 4 && <Step4Concerns {...stepProps} fieldErrors={fieldErrors} />}
          </>
        )}
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
        <Button disabled={activeStep === 0 || isSubmitting || submitSuccess} onClick={handleBack}>
          {t('buttons.back')}
        </Button>
        {activeStep === steps.length - 1 ? (
          <Button 
            variant="contained" 
            color="primary" 
            onClick={attemptSubmit} 
            disabled={isSubmitting || submitSuccess}
          >
            {isSubmitting ? <CircularProgress size={24} /> : t('buttons.submit')}
          </Button>
        ) : (
          <Button 
            variant="contained" 
            onClick={handleNext} 
            disabled={isSubmitting || submitSuccess}
          >
            {t('buttons.next')}
          </Button>
        )}
      </Box>
    </Box>
  );
}
